// Consent management utility
export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  timestamp: number;
}

export const CONSENT_STORAGE_KEY = 'hm_consent_preferences';

// Default consent preferences
export const defaultConsentPreferences: ConsentPreferences = {
  analytics: false,
  marketing: false,
  functional: false,
  timestamp: 0,
};

// Get stored consent preferences
export function getConsentPreferences(): ConsentPreferences | null {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!stored) return null;
    
    const parsed = JSON.parse(stored) as ConsentPreferences;
    
    // Check if consent is older than 13 months (GDPR requirement)
    const thirteenMonthsAgo = Date.now() - (13 * 30 * 24 * 60 * 60 * 1000);
    if (parsed.timestamp < thirteenMonthsAgo) {
      localStorage.removeItem(CONSENT_STORAGE_KEY);
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Error reading consent preferences:', error);
    return null;
  }
}

// Save consent preferences
export function saveConsentPreferences(preferences: Omit<ConsentPreferences, 'timestamp'>): void {
  try {
    const preferencesWithTimestamp: ConsentPreferences = {
      ...preferences,
      timestamp: Date.now(),
    };
    
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferencesWithTimestamp));
    
    // Update Google Consent Mode
    updateGoogleConsent(preferences);
  } catch (error) {
    console.error('Error saving consent preferences:', error);
  }
}

// Update Google Consent Mode based on preferences
export function updateGoogleConsent(preferences: Omit<ConsentPreferences, 'timestamp'>): void {
  if (typeof window !== 'undefined') {
    // Use dataLayer directly if gtag is not available yet
    const consentUpdate = {
      'analytics_storage': preferences.analytics ? 'granted' : 'denied',
      'ad_storage': preferences.marketing ? 'granted' : 'denied',
      'ad_user_data': preferences.marketing ? 'granted' : 'denied',
      'ad_personalization': preferences.marketing ? 'granted' : 'denied',
      'functionality_storage': preferences.functional ? 'granted' : 'denied',
      'personalization_storage': preferences.functional ? 'granted' : 'denied',
    };

    if (window.gtag) {
      window.gtag('consent', 'update', consentUpdate);
    } else {
      // Fallback: push directly to dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(['consent', 'update', consentUpdate]);
      console.log('Consent updated via dataLayer (gtag not available):', consentUpdate);
    }
  }
}

// Check if consent banner should be shown
export function shouldShowConsentBanner(): boolean {
  return getConsentPreferences() === null;
}

// Accept all cookies
export function acceptAllCookies(): void {
  saveConsentPreferences({
    analytics: true,
    marketing: true,
    functional: true,
  });
}

// Reject all non-essential cookies
export function rejectAllCookies(): void {
  saveConsentPreferences({
    analytics: false,
    marketing: false,
    functional: false,
  });
}

// Declare gtag function for TypeScript
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}
